import React, {useEffect, useRef, useState} from 'react';
import {
    App,
    Badge,
    Button,
    Card,
    Col,
    Divider,
    Modal,
    Popover,
    Row,
    Space,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {
    BranchesOutlined,
    ClockCircleOutlined,
    CopyOutlined,
    DatabaseOutlined,
    DeleteOutlined,
    DownOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    LineChartOutlined,
    LinkOutlined,
    RedoOutlined,
    RobotOutlined,
    SwapOutlined,
    UnorderedListOutlined,
    UpOutlined
} from '@ant-design/icons';
import SegmentEditor from '../../components/bot/SegmentEditor';
import {
    ActionHistoryResponse,
    botService,
    ForecastSummaryResponse,
    parsePromptToSegments,
    PromptHistoryResponse,
    SegmentModel
} from '../../services/botService';
import {TradingOrder, tradingService} from '../../services/tradingService';
import RiskControlInfoModal from '../../components/risk-control/RiskControlInfoModal';
import KlineForecastModal from '../../components/bot/KlineForecastModal';
import AgentExecutionPromptView from './AgentExecutionPromptView';
import './PromptItem.css';

const {Text, Title} = Typography;

/**
 * 单个item的展开状态接口
 * 所有字段都是必需的，确保展开状态正确传递
 */
interface ItemExpandedState {
    promptContent: boolean; // Prompt内容是否展开
    aiResponse: boolean;   // AI模型响应是否展开
}

interface PromptItemProps {
    item: PromptHistoryResponse;
    onCallModel?: (decisionId: string) => void;
    expandedState?: ItemExpandedState; // 从父组件传入的展开状态
    onExpandedStateChange?: (key: keyof ItemExpandedState, value: boolean) => void; // 状态变化回调
    onParentIdClick?: (parentId: number) => void; // 点击parentId的回调
    publicView?: boolean; // 公开展示模式，隐藏重放等需登录的功能
    onTrackEvolutionClick?: (link: { trackUnitId: number; trackRunId: number }) => void;
    readOnly?: boolean;
    hideTrackActionTag?: boolean;
    apiKeyId?: number | null;
    deletable?: boolean;
    onDelete?: (item: PromptHistoryResponse) => void;
}

type TrackTradePresentation = Pick<PromptHistoryResponse,
    'trackRunId' | 'openCloses' | 'posSide' | 'instId' | 'price' | 'quantity' | 'reasoning'>;

/**
 * Track 调仓不生成 TradeAction。为 Prompt 历史卡片补一个只用于展示的买卖动作：
 * 平多 = 卖出，平空 = 买入。原始 TRACK 动作仍保留用于详情和数据语义。
 */
export function withTrackTradePresentation(actionObjects: any[], item: TrackTradePresentation): any[] {
    const isClose = item.openCloses?.split(',').some(value => value.trim().toLowerCase() === 'close');
    const alreadyHasTradeAction = actionObjects.some(action =>
        ['BUY', 'SELL'].includes(String(action.action || '').toUpperCase()));
    if (!item.trackRunId || !isClose || alreadyHasTradeAction) return actionObjects;

    const posSide = item.posSide?.toLowerCase();
    const action = posSide === 'long' ? 'SELL' : posSide === 'short' ? 'BUY' : null;
    if (!action) return actionObjects;
    return [...actionObjects, {
        action,
        instId: item.instId,
        posSide: item.posSide,
        openClose: 'close',
        price: item.price,
        quantity: item.quantity,
        reasoning: item.reasoning,
    }];
}

/**
 * PromptItem组件
 * 实现简化折叠结构：
 * - 卡片始终展开（移除第一层折叠）
 * - 第二层默认折叠：Prompt内容 & AI模型响应标题
 * - 支持受控的展开状态，避免数据刷新时状态丢失
 * - AI模型响应使用后端解析的responseSegments字段
 */
const PromptItem: React.FC<PromptItemProps> = ({
                                                   item,
                                                   onCallModel,
                                                   expandedState,
                                                   onExpandedStateChange,
                                                   onParentIdClick,
                                                   publicView,
                                                   onTrackEvolutionClick,
                                                   hideTrackActionTag = false,
                                                   apiKeyId,
                                                   deletable = false,
                                                   onDelete,
                                               }) => {
    const {message: messageApi} = App.useApp();
    const [callingModel, setCallingModel] = useState(false);
    const [promptDisplayMode, setPromptDisplayMode] = useState<'segments' | 'raw'>('segments');
    const [aiResponseDisplayMode, setAiResponseDisplayMode] = useState<'segments' | 'raw'>('segments');
    const [replaying, setReplaying] = useState(false);
    // Action执行历史状态
    const [actionHistoryMap, setActionHistoryMap] = useState<Record<number, ActionHistoryResponse[]>>({});
    const [loadingHistoryMap, setLoadingHistoryMap] = useState<Record<number, boolean>>({});
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const [activeTabKey, setActiveTabKey] = useState<string>('aiResponse');
    const [relatedOrders, setRelatedOrders] = useState<TradingOrder[]>([]);
    const [relatedLoading, setRelatedLoading] = useState<boolean>(false);
    // Reasoning Modal状态
    const [reasoningModalVisible, setReasoningModalVisible] = useState(false);
    const [currentReasoning, setCurrentReasoning] = useState<string>('');
    // 折叠状态 - 默认折叠
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    // 删除蒙层状态：悬停满5s显示
    const [deleteOverlayVisible, setDeleteOverlayVisible] = useState(false);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hoveringRef = useRef(false);

    // 卸载时清理悬停计时器
    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
            }
        };
    }, []);

    const handleMouseEnter = () => {
        if (!deletable) return;
        hoveringRef.current = true;
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
        }
        hoverTimerRef.current = setTimeout(() => {
            if (hoveringRef.current) {
                setDeleteOverlayVisible(true);
            }
        }, 5000);
    };

    const handleMouseLeave = () => {
        hoveringRef.current = false;
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
        setDeleteOverlayVisible(false);
    };

    const handleDelete = () => {
        Modal.confirm({
            title: <span style={{color: '#ffffff'}}>删除该条Prompt记录？</span>,
            className: 'bot-confirm-modal',
            centered: true,
            width: 560,
            content: <div
                style={{color: '#d9d9d9'}}>将逻辑删除该条决策相关的记录（决策内容、会话消息、余额快照、预期K线等），不影响实际仓位与真实订单。</div>,
            okText: '删除',
            cancelText: '取消',
            okButtonProps: {danger: true},
            onOk: async () => {
                try {
                    const res = await botService.deletePrompt(item.decisionId, apiKeyId ?? undefined);
                    if (res.success) {
                        messageApi.success('已删除');
                        onDelete?.(item);
                    } else {
                        messageApi.error(res.message || '删除失败');
                    }
                } catch (e) {
                    messageApi.error('删除失败');
                }
            },
        });
    };

    // 【新增】存储从promptContent解析得到的segments
    const [parsedSegments, setParsedSegments] = useState<SegmentModel[]>([]);

    // 订单详情弹窗状态
    const [orderDetailVisible, setOrderDetailVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<TradingOrder | null>(null);

    // 风控信息弹窗状态
    const [riskControlInfoVisible, setRiskControlInfoVisible] = useState(false);
    const [riskControlRecordId, setRiskControlRecordId] = useState<number | null>(null);
    const [riskControlApiKeyId, setRiskControlApiKeyId] = useState<number | null>(null);
    // K线预测展示状态
    const [forecastVisible, setForecastVisible] = useState(false);
    const [forecastSummaries, setForecastSummaries] = useState<ForecastSummaryResponse[]>([]);
    const [forecastSummaryLoading, setForecastSummaryLoading] = useState(false);
    const hasForecastCandidate = ['BUY', 'SELL'].some(action =>
        (item.action || '').includes(action)
        || (item.fullResponse || '').includes(`"action":"${action}"`)
        || (item.fullResponse || '').includes(`"action": "${action}"`)
        || (item.responseSegments || []).some(segment =>
            segment.title === '决策JSON' && segment.content.includes(action))
    );
    const isUserOnlyPrompt = item.promptViewMode === 'USER_ONLY';
    const promptTitle = isUserOnlyPrompt ? '用户设定' : item.promptViewMode === 'FULL' ? '完整Prompt' : 'Prompt内容';
    const hasPromptSections = !!(item.promptSections && item.promptSections.length > 0);

    const buildPromptSectionsCopyText = () => {
        return item.promptSections?.map(section =>
            `=== ${section.title} ===\n${section.content || ''}`
        ).join('\n\n') || '';
    };

    // 格式化时间
    const formatTime = (timestamp: any) => {
        if (!timestamp) return '无时间记录';
        try {
            // 处理数组格式时间 [year, month, day, hour, minute, second]
            if (Array.isArray(timestamp)) {
                // Java月份是1-12，JS Date月份是0-11
                const date = new Date(
                    timestamp[0],
                    timestamp[1] - 1,
                    timestamp[2],
                    timestamp[3] || 0,
                    timestamp[4] || 0,
                    timestamp[5] || 0
                );
                return date.toLocaleString('zh-CN');
            }
            const date = new Date(timestamp);
            return date.toLocaleString('zh-CN');
        } catch {
            return '时间解析错误';
        }
    };

    // 【新增】解析promptContent为segments（如果item.segments不存在）
    // 使用useEffect确保在item变化时自动解析
    React.useEffect(() => {
        if (item.promptContent) {
            if (isUserOnlyPrompt && !item.promptContent.includes('===')) {
                setParsedSegments([]);
                return;
            }
            // 如果item.segments存在且不为空，使用它（向后兼容）
            if (item.segments && item.segments.length > 0) {
                setParsedSegments(item.segments);
            } else {
                // 否则，从promptContent动态解析
                const segments = parsePromptToSegments(item.promptContent);
                setParsedSegments(segments);
            }
        } else {
            setParsedSegments([]);
        }
    }, [item.promptContent, item.segments, isUserOnlyPrompt]);

    React.useEffect(() => {
        if (!isExpanded || !hasForecastCandidate || item.status !== 'SUCCESS') {
            return;
        }
        let active = true;
        setForecastSummaryLoading(true);
        botService.getForecastsByDecision(item.decisionId).then(response => {
            if (active && response.success) {
                setForecastSummaries(response.data || []);
            }
        }).finally(() => {
            if (active) setForecastSummaryLoading(false);
        });
        return () => {
            active = false;
        };
    }, [isExpanded, hasForecastCandidate, item.decisionId, item.status]);

    // 打开订单详情弹窗
    const handleOpenOrderDetail = (order: TradingOrder) => {
        setSelectedOrder(order);
        setOrderDetailVisible(true);
    };

    // 关闭订单详情弹窗
    const handleCloseOrderDetail = () => {
        setOrderDetailVisible(false);
        setSelectedOrder(null);
    };

    // 渲染订单详情弹窗
    const renderOrderDetailModal = () => {
        if (!selectedOrder) return null;

        const {cexOrder} = selectedOrder;

        const DetailItem = ({label, value, copyable = false}: {
            label: string,
            value: React.ReactNode,
            copyable?: boolean
        }) => (
            <div style={{marginBottom: '12px', display: 'flex', flexDirection: 'column'}}>
                <Text style={{color: '#8c8c8c', fontSize: '12px', marginBottom: '4px'}}>{label}</Text>
                <Text
                    style={{color: '#d9d9d9', fontSize: '14px'}}
                    copyable={copyable ? {text: String(value)} : false}
                >
                    {value}
                </Text>
            </div>
        );

        return (
            <Modal
                title={<span style={{color: '#fff'}}>订单详情</span>}
                open={orderDetailVisible}
                onCancel={handleCloseOrderDetail}
                footer={null}
                width={600}
                className="dark-theme-modal"
                styles={{body: {padding: '24px'}}}
            >
                <div style={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
                    {/* 基础信息 */}
                    <div>
                        <Title level={5} style={{
                            color: '#fff',
                            marginBottom: '16px',
                            borderLeft: '3px solid #F0B429',
                            paddingLeft: '8px'
                        }}>基础信息</Title>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <DetailItem label="系统订单ID" value={selectedOrder.orderUuid} copyable/>
                            </Col>
                            <Col span={12}>
                                <DetailItem label="合约代码" value={selectedOrder.instId} copyable/>
                            </Col>
                            <Col span={12}>
                                <DetailItem label="方向" value={
                                    <span
                                        style={{color: selectedOrder.side === 'buy' ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>
                                        {selectedOrder.side === 'buy' ? '买入' : '卖出'}
                                        {selectedOrder.posSide ? ` (${selectedOrder.posSide})` : ''}
                                    </span>
                                }/>
                            </Col>
                            <Col span={12}>
                                <DetailItem label="类型" value={selectedOrder.orderType}/>
                            </Col>
                            <Col span={12}>
                                <DetailItem label="状态" value={
                                    <Tag
                                        color={selectedOrder.orderStatus === 'success' ? 'success' : selectedOrder.orderStatus === 'failed' ? 'error' : 'processing'}>
                                        {selectedOrder.orderStatus}
                                    </Tag>
                                }/>
                            </Col>
                            <Col span={12}>
                                <DetailItem label="创建时间"
                                            value={new Date(selectedOrder.createdTime).toLocaleString()}/>
                            </Col>
                            {selectedOrder.errorMsg && (
                                <Col span={24}>
                                    <DetailItem label="错误信息" value={<span
                                        style={{color: 'var(--pt-bear)'}}>{selectedOrder.errorMsg}</span>}/>
                                </Col>
                            )}
                        </Row>
                    </div>

                    {/* CEX订单信息 */}
                    {cexOrder && (
                        <div>
                            <Title level={5} style={{
                                color: '#fff',
                                marginBottom: '16px',
                                borderLeft: '3px solid var(--pt-amber)',
                                paddingLeft: '8px'
                            }}>交易所订单详情</Title>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <DetailItem label="交易所订单ID" value={cexOrder.orderId} copyable/>
                                </Col>
                                <Col span={12}>
                                    <DetailItem label="交易所状态" value={cexOrder.orderState}/>
                                </Col>
                                <Col span={12}>
                                    <DetailItem label="委托价格" value={cexOrder.px}/>
                                </Col>
                                <Col span={12}>
                                    <DetailItem label="成交均价" value={cexOrder.avgPx || '-'}/>
                                </Col>
                                <Col span={12}>
                                    <DetailItem label="委托数量" value={cexOrder.sz}/>
                                </Col>
                                <Col span={12}>
                                    <DetailItem label="成交数量" value={cexOrder.filledSz}/>
                                </Col>
                                <Col span={12}>
                                    <DetailItem label="手续费" value={`${cexOrder.fee} ${cexOrder.feeCcy || ''}`}/>
                                </Col>
                                <Col span={12}>
                                    <DetailItem label="更新时间"
                                                value={cexOrder.uTime ? new Date(Number(cexOrder.uTime)).toLocaleString() : '-'}/>
                                </Col>
                            </Row>
                        </div>
                    )}
                </div>
            </Modal>
        );
    };

    // 获取风险等级颜色
    const getRiskColor = (riskLevel: string) => {
        switch (riskLevel?.toUpperCase()) {
            case 'LOW':
                return 'green';
            case 'MEDIUM':
                return 'orange';
            case 'HIGH':
                return 'red';
            default:
                return 'default';
        }
    };

    // 获取风险等级文本
    const getRiskText = (riskLevel: string) => {
        switch (riskLevel?.toUpperCase()) {
            case 'LOW':
                return '低风险';
            case 'MEDIUM':
                return '中等风险';
            case 'HIGH':
                return '高风险';
            default:
                return '未评估';
        }
    };

    /**
     * 获取状态Badge的status属性
     * @param status 状态值
     * @returns Badge的status属性
     */
    const getStatusBadgeStatus = (status: string): 'success' | 'processing' | 'error' | 'warning' | 'default' => {
        switch (status) {
            case 'SUCCESS':
                return 'success';
            case 'PROCESSING':
                return 'processing';
            case 'FAILED':
                return 'error';
            case 'AGENT_DISABLED':
                return 'warning';
            default:
                return 'default';
        }
    };

    /**
     * 获取状态显示文本
     * @param status 状态值
     * @returns 状态显示文本
     */
    const getStatusText = (status: string): string => {
        switch (status) {
            case 'SUCCESS':
                return '成功';
            case 'PROCESSING':
                return '处理中';
            case 'FAILED':
                return '失败';
            case 'AGENT_DISABLED':
                return '已禁用';
            default:
                return '未知';
        }
    };

    /**
     * 获取调用来源Tag的颜色
     * @param source 调用来源
     * @returns Tag颜色
     */
    const getSourceColor = (source: string | null | undefined): string => {
        if (!source) return 'default';
        switch (source) {
            case 'SCHEDULED':
                return 'blue';
            case 'MANUAL':
                return 'green';
            case 'DIRECT':
                return 'orange';
            case 'AGENT_TRIGGER':
                return 'gold';
            case 'TRACK_SCHEDULED':
                return 'cyan';
            default:
                return 'default';
        }
    };

    /**
     * 获取调用来源显示文本
     * @param source 调用来源
     * @returns 显示文本
     */
    const getSourceText = (source: string | null | undefined): string => {
        if (!source) return '未知来源';
        switch (source) {
            case 'SCHEDULED':
                return 'AI';
            case 'MANUAL':
                return 'HUM';
            case 'DIRECT':
                return 'BOT';
            case 'AGENT_TRIGGER':
                return 'Agent';
            case 'TRACK_SCHEDULED':
                return 'Track';
            default:
                return '未知来源';
        }
    };

    /**
     * 获取Track持仓方向的展示文本和颜色。
     * long → 绿/多，short → 红/空。
     */
    const getTrackDirectionMeta = (posSide: string | null | undefined): { text: string; color: string } => {
        const side = posSide?.toLowerCase();
        if (side === 'long') {
            return {text: '多', color: 'var(--pt-bull)'};
        }
        if (side === 'short') {
            return {text: '空', color: 'var(--pt-bear)'};
        }
        return {text: '', color: '#d9d9d9'};
    };

    /**
     * 获取action对应的Tag颜色
     * @param action 决策动作
     * @returns Tag颜色
     */
    const getActionColor = (action: string | null | undefined): string => {
        if (!action) return 'default';
        switch (action.toUpperCase()) {
            case 'BUY':
                return 'green';
            case 'SELL':
                return 'red';
            case 'HOLD':
                return 'orange';
            case 'WATCH':
                return 'blue';
            case 'QUERY':
                return 'blue';
            case 'ATTENTION':
                return 'purple';
            case 'CANCEL_ORDER':
                return 'magenta';
            case 'TRACK':
                return 'cyan';
            default:
                return 'default';
        }
    };

    /**
     * 获取instId的颜色（根据持仓方向）
     * @param instId 交易对ID
     * @param posSide 持仓方向 (long/short)
     * @returns 颜色代码
     */
    const getInstIdColor = (instId: string, posSide: string): string => {
        if (posSide === 'long') return 'var(--pt-bull)';  // 绿色
        if (posSide === 'short') return 'var(--pt-bear)'; // 红色
        return '#a0a0a0'; // 灰色（默认）
    };

    /**
     * 获取置信度的颜色
     * @param confidence 置信度值
     * @returns 颜色代码
     */
    const getConfidenceColor = (confidence: any): string => {
        if (confidence === '-' || confidence === undefined || confidence === null) {
            return '#a0a0a0';
        }
        // 后端confidence已经是0-100的范围，直接使用
        const confValue = typeof confidence === 'number' ? confidence : parseFloat(confidence);
        if (confValue < 60) return 'var(--pt-bear)'; // 红色
        if (confValue < 80) return 'var(--pt-amber)'; // 橙色
        return 'var(--pt-bull)'; // 绿色
    };

    /**
     * 格式化置信度显示
     * @param confidence 置信度值
     * @returns 格式化后的字符串
     */
    const formatConfidence = (confidence: any): string => {
        if (confidence === '-' || confidence === undefined || confidence === null) {
            return '-';
        }
        // 后端confidence已经是0-100的范围，直接使用
        const confValue = typeof confidence === 'number' ? confidence : parseFloat(confidence);
        return confValue.toFixed(0) + '%';
    };

    /**
     * 获取action显示文本
     * @param action 决策动作
     * @returns 显示文本
     */
    const getActionText = (action: string | null | undefined): string => {
        if (!action) return '未知';
        switch (action.toUpperCase()) {
            case 'BUY':
                return '买入';
            case 'SELL':
                return '卖出';
            case 'HOLD':
                return '持有';
            case 'WATCH':
                return '观望';
            case 'QUERY':
                return '查询';
            case 'CANCEL_ORDER':
                return '取消挂单'
            case 'ATTENTION':
                return '关注';
            case 'TRACK':
                return 'Track跟踪';
            default:
                return action;
        }
    };

    /**
     * 获取交易动作的组合展示文本：
     * BUY+OPEN → 买入开多、SELL+CLOSE → 卖出平多、
     * SELL+OPEN → 卖出开空、BUY+CLOSE → 买入平空。
     */
    const getTradeActionText = (action: any): string => {
        const actionType = String(action?.action || '').toUpperCase();
        const openClose = String(action?.openClose || '').toUpperCase();
        const direction = String(action?.direction || action?.posSide || '').toUpperCase();

        if (actionType === 'BUY') {
            if (openClose === 'CLOSE' || direction === 'SHORT') {
                return '买入平空';
            }
            return '买入开多';
        }
        if (actionType === 'SELL') {
            if (openClose === 'CLOSE' || direction === 'LONG') {
                return '卖出平多';
            }
            return '卖出开空';
        }
        return getActionText(actionType);
    };

    const getActionGroupKey = (action: any): string => {
        const actionType = String(action?.action || '').toUpperCase();
        // 键格式：type|openClose|direction|instId（BUY/SELL）；下游均取 split('|')[0] 作类型
        if (actionType === 'BUY' || actionType === 'SELL') {
            const instId = String(action?.instId || '').trim().toUpperCase();
            return `${actionType}|${String(action?.openClose || '').toUpperCase()}|${String(action?.direction || action?.posSide || '').toUpperCase()}|${instId}`;
        }
        return actionType;
    };

    const isTradeActionKey = (key: string): boolean => {
        const base = key.split('|')[0];
        return base === 'BUY' || base === 'SELL' || base === 'CANCEL_ORDER';
    };

    /**
     * 获取大模型调用状态的颜色
     * @param status 状态值
     * @returns 颜色值
     */
    const getStatusColor = (status: string): string => {
        if (!status) return 'default';
        switch (status.toUpperCase()) {
            case 'SUCCESS':
                return 'success';
            case 'FAILED':
            case 'ERROR':
                return 'error';
            case 'AGENT_DISABLED':
                return 'warning';
            case 'PENDING':
                return 'default';
            case 'PROCESSING':
                return 'processing';
            default:
                return 'default';
        }
    };

    /**
     * 获取风控状态的颜色
     * @param status 风控状态（使用后端原始值：PENDING/APPROVED/REJECTED/BYPASSED）
     * @returns 颜色值
     */
    const getRiskControlStatusColor = (status: string | null | undefined): string => {
        if (!status) return 'default';
        switch (status) {
            case 'PENDING':
                return 'default';      // 灰色 - 待审核
            case 'APPROVED':
                return 'success';      // 绿色 - 通过
            case 'REJECTED':
                return 'error';        // 红色 - 驳回
            case 'BYPASSED':
                return 'warning';      // 橙色 - 绕过
            default:
                return 'default';
        }
    };

    /**
     * 获取风控状态的文本
     * @param status 风控状态（使用后端原始值：PENDING/APPROVED/REJECTED/BYPASSED）
     * @returns 状态文本
     */
    const getRiskControlStatusText = (status: string | null | undefined): string => {
        if (!status) return '';
        switch (status) {
            case 'PENDING':
                return '待审核';
            case 'APPROVED':
                return '核准';
            case 'REJECTED':
                return '驳回';
            case 'BYPASSED':
                return '自动核准';
            default:
                return '';
        }
    };

    /**
     * 获取交易动作状态的颜色
     * @param status 交易动作状态
     * @returns 颜色值
     */
    const getTradeActionStatusColor = (status: string | null | undefined): string => {
        if (!status) return 'default';
        switch (status) {
            case 'PENDING':
                return 'default';
            case 'EXECUTING':
                return 'processing';
            case 'SUCCESS':
                return 'success';
            case 'FAILED':
                return 'error';
            case 'REJECTED':
                return 'error';
            case 'SUBMIT_UNKNOWN':
                return 'warning';
            case 'PARTIALLY_FILLED':
                return 'processing';
            case 'PARTIAL_SUCCESS':
                return 'success';
            case 'CANCELED':
                return 'default';
            default:
                return 'default';
        }
    };

    /**
     * 获取交易动作状态的文本
     * @param status 交易动作状态
     * @returns 状态文本
     */
    const getTradeActionStatusText = (status: string | null | undefined): string => {
        if (!status) return '';
        switch (status) {
            case 'PENDING':
                return '待执行';
            case 'EXECUTING':
                return '执行中';
            case 'SUCCESS':
                return '执行成功';
            case 'FAILED':
                return '执行失败';
            case 'REJECTED':
                return '未执行';
            case 'SUBMIT_UNKNOWN':
                return '提交待确认';
            case 'PARTIALLY_FILLED':
                return '部分成交';
            case 'PARTIAL_SUCCESS':
                return '部分成交完成';
            case 'CANCELED':
                return '已撤销';
            default:
                return '';
        }
    };

    /**
     * 渲染动作详情的 Popover 内容
     * @param actions 单个动作对象或动作对象数组
     * @returns Popover 内容的 JSX 元素
     */
    const renderActionPopoverContent = (actions: any | any[]) => {
        // 支持单个action或action数组
        const actionArray = Array.isArray(actions) ? actions : [actions];

        // 获取主要动作类型（假设数组中的动作类型相同）
        const mainActionType = actionArray.length > 0 ? actionArray[0].action : '';

        // 检测是否有平仓动作（用于表格宽度设置）
        // 提前定义以避免 TDZ 错误
        const hasClose = actionArray.some(action => action.openClose === 'close');

        // 根据动作类型定义不同的列结构
        let columns: any[] = [];

        if (mainActionType === 'QUERY') {
            // QUERY动作：动作 | instId | 时间帧 | 采样数
            columns = [
                {
                    title: '动作',
                    dataIndex: 'actionDisplay',
                    key: 'actionDisplay',
                    width: 120,
                },
                {
                    title: '合约',
                    dataIndex: 'instId',
                    key: 'instId',
                    width: 200,
                    render: (instId: string, record: any) => (
                        <span style={{color: getInstIdColor(instId, record.posSide)}}>
                            {instId}
                        </span>
                    ),
                },
                {
                    title: '时间帧',
                    dataIndex: 'timeframe',
                    key: 'timeframe',
                    width: 100,
                },
                {
                    title: '采样数',
                    dataIndex: 'limit',
                    key: 'limit',
                    width: 100,
                },
            ];
        } else if (mainActionType === 'BUY' || mainActionType === 'SELL') {
            // BUY/SELL动作：动作 | instId | 方向 | [数量] | 金额 | 置信度
            columns = [
                {
                    title: '动作',
                    dataIndex: 'actionDisplay',
                    key: 'actionDisplay',
                    width: 100,  // ✅ 优化: 120 → 100
                },
                {
                    title: '合约',
                    dataIndex: 'instId',
                    key: 'instId',
                    width: 150,  // ✅ 优化: 200 → 150
                    render: (instId: string, record: any) => (
                        <span style={{color: getInstIdColor(instId, record.posSide)}}>
                            {instId}
                        </span>
                    ),
                },
                {
                    title: '方向',
                    dataIndex: 'posSide',
                    key: 'posSide',
                    width: 60,  // ✅ 优化: 80 → 60
                },
            ];

            // 平仓时添加数量列
            if (hasClose) {
                columns.push({
                    title: '数量',
                    dataIndex: 'quantity',
                    key: 'quantity',
                    width: 80,  // ✅ 优化: 100 → 80
                    render: (quantity: any) => {
                        if (quantity === '-' || quantity === undefined || quantity === null) {
                            return '-';
                        }
                        return quantity;
                    },
                });
            }

            columns.push(
                {
                    title: '金额',
                    dataIndex: 'amount',
                    key: 'amount',
                    width: 80,  // ✅ 优化: 100 → 80
                    render: (amount: any) => {
                        if (amount === '-' || amount === undefined || amount === null) {
                            return '-';
                        }
                        return `${amount} ₮`;
                    },
                },
                {
                    title: '置信度',
                    dataIndex: 'confidence',
                    key: 'confidence',
                    width: 70,  // ✅ 优化: 80 → 70
                    render: (confidence: any) => (
                        <span style={{color: getConfidenceColor(confidence)}}>
                            {formatConfidence(confidence)}
                        </span>
                    ),
                },
                {
                    title: '操作',
                    key: 'action',
                    width: 90,
                    render: (_: any, record: any) => (
                        <Space size={8}>
                            <Tooltip title="查看执行历史">
                                <UnorderedListOutlined
                                    style={{cursor: 'pointer', color: 'var(--pt-primary)'}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLoadActionHistory(record.id);
                                    }}
                                />
                            </Tooltip>
                            {record.reasoning && (
                                <Tooltip title={record.reasoning.length > 100 ? "查看推理过程" : record.reasoning}>
                                    <FileTextOutlined
                                        style={{cursor: 'pointer', color: 'var(--pt-purple)'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (record.reasoning.length > 100) {
                                                setCurrentReasoning(record.reasoning);
                                                setReasoningModalVisible(true);
                                            }
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {!publicView && (
                                <Tooltip title="重放此决策">
                                    <RedoOutlined
                                        style={{cursor: 'pointer', color: 'var(--pt-primary)'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSingleActionReplay(item.decisionId, record.id, record);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Space>
                    ),
                }
            );
        } else if (mainActionType === 'WATCH') {
            // WATCH动作：动作 | 合约 | 置信度 | 操作
            columns = [
                {
                    title: '动作',
                    dataIndex: 'actionDisplay',
                    key: 'actionDisplay',
                    width: 120,
                },
                {
                    title: '合约',
                    dataIndex: 'instId',
                    key: 'instId',
                    width: 200,
                    render: (instId: string, record: any) => (
                        <span style={{color: getInstIdColor(instId, record.posSide)}}>
                            {instId}
                        </span>
                    ),
                },
                {
                    title: '置信度',
                    dataIndex: 'confidence',
                    key: 'confidence',
                    width: 80,
                    render: (confidence: any) => (
                        <span style={{color: getConfidenceColor(confidence)}}>
                            {formatConfidence(confidence)}
                        </span>
                    ),
                },
                {
                    title: '操作',
                    key: 'action',
                    width: 90,
                    render: (_: any, record: any) => (
                        <Space size={8}>
                            <Tooltip title="查看执行历史">
                                <UnorderedListOutlined
                                    style={{cursor: 'pointer', color: 'var(--pt-primary)'}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLoadActionHistory(record.id);
                                    }}
                                />
                            </Tooltip>
                            {record.reasoning && (
                                <Tooltip title={record.reasoning.length > 100 ? "查看推理过程" : record.reasoning}>
                                    <FileTextOutlined
                                        style={{cursor: 'pointer', color: 'var(--pt-purple)'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (record.reasoning.length > 100) {
                                                setCurrentReasoning(record.reasoning);
                                                setReasoningModalVisible(true);
                                            }
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {!publicView && (
                                <Tooltip title="重放此决策">
                                    <RedoOutlined
                                        style={{cursor: 'pointer', color: 'var(--pt-primary)'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSingleActionReplay(item.decisionId, record.id, record);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Space>
                    ),
                },
            ];
        } else if (mainActionType === 'HOLD') {
            // HOLD动作：动作 | instId | 方向 | 置信度
            columns = [
                {
                    title: '动作',
                    dataIndex: 'actionDisplay',
                    key: 'actionDisplay',
                    width: 120,
                },
                {
                    title: '合约',
                    dataIndex: 'instId',
                    key: 'instId',
                    width: 200,
                    render: (instId: string, record: any) => (
                        <span style={{color: getInstIdColor(instId, record.posSide)}}>
                            {instId}
                        </span>
                    ),
                },
                {
                    title: '方向',
                    dataIndex: 'posSide',
                    key: 'posSide',
                    width: 80,
                },
                {
                    title: '置信度',
                    dataIndex: 'confidence',
                    key: 'confidence',
                    width: 80,
                    render: (confidence: any) => (
                        <span style={{color: getConfidenceColor(confidence)}}>
                            {formatConfidence(confidence)}
                        </span>
                    ),
                },
                {
                    title: '操作',
                    key: 'action',
                    width: 90,
                    render: (_: any, record: any) => (
                        <Space size={8}>
                            <Tooltip title="查看执行历史">
                                <UnorderedListOutlined
                                    style={{cursor: 'pointer', color: 'var(--pt-primary)'}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLoadActionHistory(record.id);
                                    }}
                                />
                            </Tooltip>
                            {record.reasoning && (
                                <Tooltip title={record.reasoning.length > 100 ? "查看推理过程" : record.reasoning}>
                                    <FileTextOutlined
                                        style={{cursor: 'pointer', color: 'var(--pt-purple)'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (record.reasoning.length > 100) {
                                                setCurrentReasoning(record.reasoning);
                                                setReasoningModalVisible(true);
                                            }
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {!publicView && (
                                <Tooltip title="重放此决策">
                                    <RedoOutlined
                                        style={{cursor: 'pointer', color: 'var(--pt-primary)'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSingleActionReplay(item.decisionId, record.id, record);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Space>
                    ),
                },
            ];
        } else if (mainActionType === 'ATTENTION') {
            // ATTENTION动作：动作 | instId | 置信度 | 操作
            columns = [
                {
                    title: '动作',
                    dataIndex: 'actionDisplay',
                    key: 'actionDisplay',
                    width: 120,
                },
                {
                    title: '合约',
                    dataIndex: 'instId',
                    key: 'instId',
                    width: 200,
                    render: (instId: string, record: any) => (
                        <span style={{color: getInstIdColor(instId, record.posSide)}}>
                            {instId}
                        </span>
                    ),
                },
                {
                    title: '置信度',
                    dataIndex: 'confidence',
                    key: 'confidence',
                    width: 80,
                    render: (confidence: any) => (
                        <span style={{color: getConfidenceColor(confidence)}}>
                            {formatConfidence(confidence)}
                        </span>
                    ),
                },
                {
                    title: '操作',
                    key: 'action',
                    width: 90,
                    render: (_: any, record: any) => (
                        <Space size={8}>
                            <Tooltip title="查看执行历史">
                                <UnorderedListOutlined
                                    style={{cursor: 'pointer', color: 'var(--pt-primary)'}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLoadActionHistory(record.id);
                                    }}
                                />
                            </Tooltip>
                            {record.reasoning && (
                                <Tooltip title={record.reasoning.length > 100 ? "查看推理过程" : record.reasoning}>
                                    <FileTextOutlined
                                        style={{cursor: 'pointer', color: 'var(--pt-purple)'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (record.reasoning.length > 100) {
                                                setCurrentReasoning(record.reasoning);
                                                setReasoningModalVisible(true);
                                            }
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {!publicView && (
                                <Tooltip title="重放此决策">
                                    <RedoOutlined
                                        style={{cursor: 'pointer', color: 'var(--pt-primary)'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSingleActionReplay(item.decisionId, record.id, record);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Space>
                    ),
                },
            ];
        } else {
            // CANCEL等动作：动作 | instId | 方向 | 置信度
            columns = [
                {
                    title: '动作',
                    dataIndex: 'actionDisplay',
                    key: 'actionDisplay',
                    width: 120,
                },
                {
                    title: '合约',
                    dataIndex: 'instId',
                    key: 'instId',
                    width: 200,
                    render: (instId: string, record: any) => (
                        <span style={{color: getInstIdColor(instId, record.posSide)}}>
                            {instId}
                        </span>
                    ),
                },
                {
                    title: '方向',
                    dataIndex: 'posSide',
                    key: 'posSide',
                    width: 80,
                },
                {
                    title: '置信度',
                    dataIndex: 'confidence',
                    key: 'confidence',
                    width: 80,
                    render: (confidence: any) => (
                        <span style={{color: getConfidenceColor(confidence)}}>
                            {formatConfidence(confidence)}
                        </span>
                    ),
                },
                {
                    title: '操作',
                    key: 'action',
                    width: 90,
                    render: (_: any, record: any) => (
                        <Space size={8}>
                            <Tooltip title="查看执行历史">
                                <UnorderedListOutlined
                                    style={{cursor: 'pointer', color: 'var(--pt-primary)'}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLoadActionHistory(record.id);
                                    }}
                                />
                            </Tooltip>
                            {record.reasoning && (
                                <Tooltip title={record.reasoning.length > 100 ? "查看推理过程" : record.reasoning}>
                                    <FileTextOutlined
                                        style={{cursor: 'pointer', color: 'var(--pt-purple)'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (record.reasoning.length > 100) {
                                                setCurrentReasoning(record.reasoning);
                                                setReasoningModalVisible(true);
                                            }
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {!publicView && (
                                <Tooltip title="重放此决策">
                                    <RedoOutlined
                                        style={{cursor: 'pointer', color: 'var(--pt-primary)'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSingleActionReplay(item.decisionId, record.id, record);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Space>
                    ),
                },
            ];
        }

        // 准备表格数据源（支持多个action）
        const dataSource = actionArray.map((action, index) => ({
            key: String(action.id), // 使用action.id作为key,与expandedRowKeys匹配
            id: action.id, // 保留原始action的id字段，用于action重放功能
            actionDisplay: getActionText(action.action),
            instId: action.instId || '-',
            posSide: action.posSide || '-',
            amount: action.amount || '-',
            confidence: action.confidence !== undefined ? action.confidence : '-',
            timeframe: action.timeframe || '-',
            limit: action.limit !== undefined ? action.limit : '-',
            quantity: action.quantity !== undefined ? action.quantity : '-',
            openClose: action.openClose || '-',
            reasoning: action.reasoning, // 添加reasoning字段，用于reasoning图标显示
        }));

        return (
            <div style={{overflowX: 'auto', maxWidth: '100%'}}>
                {/* 表格显示动作详情 */}
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    size="small"
                    bordered={false}
                    showHeader={true}
                    tableLayout="fixed"
                    style={{
                        background: 'var(--pt-bg-panel)',
                        fontSize: '12px',
                        width: '100%',
                        minWidth: hasClose ? '550px' : '470px'  // ✅ 设置最小宽度
                    }}
                    className="dark-theme-action-table"
                    expandable={{
                        expandedRowRender: (record: any) => {
                            const actionHistory = actionHistoryMap[record.id];
                            const loading = loadingHistoryMap[record.id];

                            return (
                                <div style={{
                                    padding: '16px',
                                    background: 'var(--pt-bg-hover)',
                                    overflowX: 'auto',
                                    maxWidth: '100%'
                                }}>
                                    {loading ? (
                                        <div style={{textAlign: 'center', padding: '20px'}}>
                                            <span style={{color: '#a0a0a0'}}>加载中...</span>
                                        </div>
                                    ) : (
                                        <Table
                                            rowKey="actionId"
                                            columns={[
                                                {
                                                    title: 'Action ID',
                                                    dataIndex: 'actionId',
                                                    key: 'actionId',
                                                    width: 90,
                                                    render: (id: number) => (
                                                        <span style={{
                                                            color: 'var(--pt-primary)',
                                                            fontFamily: 'monospace'
                                                        }}>
                                                            #{id}
                                                        </span>
                                                    )
                                                },
                                                {
                                                    title: '来源',
                                                    dataIndex: 'executionSource',
                                                    key: 'executionSource',
                                                    width: 70,
                                                    render: (source: string) => {
                                                        const color = source === 'INITIAL' ? 'var(--pt-bull)' : 'var(--pt-amber)';
                                                        return <span style={{color}}>{source}</span>;
                                                    }
                                                },
                                                {
                                                    title: '创建时间',
                                                    dataIndex: 'createdTime',
                                                    key: 'createdTime',
                                                    width: 150,
                                                    render: (timestamp: number | null) => (
                                                        <span style={{color: '#a0a0a0', fontSize: '11px'}}>
                                                            {timestamp ? formatTime(timestamp) : '-'}
                                                        </span>
                                                    )
                                                },
                                                {
                                                    title: '状态',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 70,
                                                    render: (status: string) => {
                                                        const statusConfig: Record<string, {
                                                            text: string;
                                                            color: string
                                                        }> = {
                                                            'PENDING': {text: '待执行', color: 'var(--pt-amber)'},
                                                            'SUCCESS': {text: '成功', color: 'var(--pt-bull)'},
                                                            'FAILED': {text: '失败', color: 'var(--pt-bear)'},
                                                            'AGENT_DISABLED': {text: '已禁用', color: 'var(--pt-amber)'}
                                                        };
                                                        const config = statusConfig[status] || {
                                                            text: status,
                                                            color: '#a0a0a0'
                                                        };
                                                        return <span style={{color: config.color}}>{config.text}</span>;
                                                    }
                                                },
                                                {
                                                    title: '执行时间',
                                                    dataIndex: 'executedTime',
                                                    key: 'executedTime',
                                                    width: 150,
                                                    render: (time: string | null) => (
                                                        <span style={{color: '#a0a0a0', fontSize: '11px'}}>
                                                            {time || '-'}
                                                        </span>
                                                    )
                                                }
                                            ]}
                                            dataSource={actionHistory}
                                            pagination={false}
                                            size="small"
                                            bordered={false}
                                            showHeader={true}
                                            tableLayout="fixed"
                                            style={{
                                                background: 'var(--pt-bg-hover)',
                                                fontSize: '12px',
                                                width: '100%',
                                                minWidth: '530px'
                                            }}
                                            className="dark-theme-action-table"
                                        />
                                    )}
                                </div>
                            );
                        },
                        expandedRowKeys: expandedRowKeys
                    }}
                />
            </div>
        );
    };

    /**
     * 获取增强的action显示文本（动作 + 交易对 + 方向）
     * @param action 决策动作
     * @param instId 交易对ID (如 BTC-USDT-SWAP)
     * @param posSide 持仓方向 (long/short)
     * @returns 增强的显示文本
     */
    const getEnhancedActionText = (
        action: string | null | undefined,
        instId: string | null | undefined,
        posSide: string | null | undefined
    ): string => {
        // 直接返回 action 文本，不附加 instId 和 posSide
        return getActionText(action);
    };

    /**
     * 从responseSegments中解析action字段
     * @param responseSegments AI响应的结构化段落数据
     * @returns action值(BUY/SELL/HOLD/QUERY/ATTENTION),如果解析失败返回null
     */
    const parseActionFromSegments = (responseSegments?: Array<{
        title?: string;
        content?: string;
        category?: string;
    }>): string | null => {
        if (!responseSegments || responseSegments.length === 0) {
            return null;
        }

        // 查找title="决策JSON"的segment
        const decisionJsonSegment = responseSegments.find(seg => seg.title === "决策JSON");

        if (decisionJsonSegment && decisionJsonSegment.content) {
            try {
                const decisionData = normalizeActionField(parseDecisionJson(responseSegments));

                if (decisionData) {
                    if (Array.isArray(decisionData)) {
                        // 如果是数组，返回所有action的组合，用逗号分隔
                        return decisionData.map(d => d.action).filter(a => a).join(',');
                    } else if (decisionData.actions && Array.isArray(decisionData.actions)) {
                        // 如果包含actions数组
                        return decisionData.actions.map((d: any) => d.action).filter((a: any) => a).join(',');
                    } else if (decisionData.action) {
                        return decisionData.action;
                    }
                }

                // 降级策略：如果JSON解析失败，尝试直接正则匹配
                let content = decisionJsonSegment.content;
                // 提取"action":"VALUE"
                const actionMatch = content.match(/"action":\s*"([^"]+)"/);
                if (actionMatch && actionMatch[1]) {
                    // console.log('✅ 前端成功解析action(Regex):', actionMatch[1]);
                    return actionMatch[1];
                }
            } catch (error) {
                // console.warn('解析action失败:', error);
            }
        }

        return null;
    };

    /**
     * 从responseSegments中解析完整决策JSON
     */
    const parseDecisionJson = (responseSegments?: Array<{
        title?: string;
        content?: string;
        category?: string;
    }>): any => {
        if (!responseSegments || responseSegments.length === 0) {
            return null;
        }

        // 查找title="决策JSON"的segment
        const decisionJsonSegment = responseSegments.find(seg => seg.title === "决策JSON");

        if (decisionJsonSegment && decisionJsonSegment.content) {
            try {
                const content = decisionJsonSegment.content;
                // 优先尝试提取markdown代码块内容
                const jsonBlockMatch = content.match(/```json\s*([\s\S]*?)\s*```/);

                if (jsonBlockMatch && jsonBlockMatch[1]) {
                    return JSON.parse(jsonBlockMatch[1].trim());
                }

                // 如果没有找到代码块，尝试直接清理后解析(兼容纯JSON情况)
                const cleanedContent = content.replace(/```json\n?/g, '').replace(/```/g, '').trim();
                // 尝试查找第一个{和最后一个}
                const firstBrace = cleanedContent.indexOf('{');
                const lastBrace = cleanedContent.lastIndexOf('}');

                if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
                    const potentialJson = cleanedContent.substring(firstBrace, lastBrace + 1);
                    return JSON.parse(potentialJson);
                }

                // 最后尝试直接解析
                return JSON.parse(cleanedContent);
            } catch (error) {
                // console.warn('解析决策JSON失败:', error);
            }
        }

        return null;
    };

    /**
     * 标准化决策JSON字段：将Agent格式的decision字段映射为action字段
     * AI返回的JSON可能使用decision字段名而非action，需要统一标准化
     * 同时也处理symbol字段映射为instId（AI可能返回symbol而非instId）
     */
    const normalizeActionField = (data: any): any => {
        if (!data) return data;

        if (Array.isArray(data)) {
            return data.map(item => normalizeActionField(item));
        }

        if (typeof data === 'object') {
            const normalized = {...data};
            // 如果有decision字段但没有action字段，复制一份
            if (normalized.decision !== undefined && normalized.action === undefined) {
                normalized.action = normalized.decision;
            }
            // 如果有symbol字段但没有instId字段，将symbol映射为instId
            // AI返回的JSON可能使用symbol字段名表示合约代码
            if (normalized.symbol !== undefined && normalized.instId === undefined) {
                normalized.instId = normalized.symbol;
            }
            // 处理嵌套的actions数组
            if (normalized.actions && Array.isArray(normalized.actions)) {
                normalized.actions = normalized.actions.map((item: any) => normalizeActionField(item));
            }
            return normalized;
        }

        return data;
    };

    // 复制功能
    const handleCopy = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            messageApi.success(`已复制${fieldName}`);
        } catch (err) {
            messageApi.error(`复制${fieldName}失败`);
            // console.error('复制失败:', err);
        }
    };

    // 重放决策
    const handleReplay = async () => {
        // 解析决策JSON以获取完整动作列表
        const decisionData = normalizeActionField(parseDecisionJson(item.responseSegments));
        let actions: any[] = [];

        if (decisionData) {
            if (Array.isArray(decisionData)) {
                actions = decisionData;
            } else if (decisionData.actions && Array.isArray(decisionData.actions)) {
                actions = decisionData.actions;
            } else if (decisionData.action) {
                // 单个动作对象
                actions = [decisionData];
            }
        }

        // 如果没有解析出动作，使用item中的摘要信息作为兜底
        if (actions.length === 0) {
            actions.push({
                instId: item.instId,
                action: item.action,
                price: item.price,
                quantity: item.quantity
            });
        }

        // 二次确认弹窗
        Modal.confirm({
            title: <span style={{color: '#ffffff'}}>确认重放决策?</span>,
            className: 'dark-theme-modal bot-confirm-modal',
            centered: true,
            width: 600,
            content: (
                <div style={{color: '#a0a0a0'}}>
                    <p style={{marginBottom: '10px'}}>您即将重放此历史决策，包含 <strong>{actions.length}</strong> 个动作:
                    </p>

                    <div style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        paddingRight: '4px'
                    }}>
                        {actions.map((action, index) => (
                            <div key={index} style={{
                                background: '#2a2a2a',
                                padding: '12px',
                                borderRadius: '6px',
                                marginBottom: '12px',
                                border: '1px solid #404040'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '8px',
                                    borderBottom: '1px solid #404040',
                                    paddingBottom: '8px'
                                }}>
                                    <span style={{color: '#fff', fontWeight: 'bold'}}>动作 #{index + 1}</span>
                                    <Tag color={getActionColor(action.action)}>{getActionText(action.action)}</Tag>
                                </div>
                                {/* 所有动作都显示合约 */}
                                {(action.instId || action.instrumentId) && (
                                    <p style={{marginBottom: '4px'}}><strong style={{color: '#d9d9d9'}}>合约:</strong>
                                        <span
                                            style={{color: '#fff'}}>{action.instId || action.instrumentId}</span></p>
                                )}
                                {/* 根据动作类型显示不同字段 */}
                                {action.action === 'CANCEL_ORDER' ? (
                                    <>
                                        {/* 取消挂单：只显示订单ID和持仓方向 */}
                                        {action.orderId && (
                                            <p style={{marginBottom: '4px'}}><strong
                                                style={{color: '#d9d9d9'}}>订单ID:</strong> <span
                                                style={{color: '#fff'}}>{action.orderId}</span></p>
                                        )}
                                        {action.posSide && (
                                            <p style={{marginBottom: '4px'}}><strong
                                                style={{color: '#d9d9d9'}}>持仓方向:</strong> <span
                                                style={{color: '#fff'}}>{action.posSide}</span></p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {/* 其他动作（开仓/平仓等）：显示核心信息 */}
                                        {action.action && (
                                            <p style={{marginBottom: '4px'}}><strong
                                                style={{color: '#d9d9d9'}}>动作:</strong> <span
                                                style={{color: '#fff'}}>{action.action}</span></p>
                                        )}
                                        {action.posSide && (
                                            <p style={{marginBottom: '4px'}}><strong
                                                style={{color: '#d9d9d9'}}>持仓方向:</strong> <span
                                                style={{color: '#fff'}}>{action.posSide}</span></p>
                                        )}
                                        {action.orderType && (
                                            <p style={{marginBottom: '4px'}}><strong
                                                style={{color: '#d9d9d9'}}>订单类型:</strong> <span
                                                style={{color: '#fff'}}>{action.orderType}</span></p>
                                        )}
                                        {(action.quantity !== null && action.quantity !== undefined) && (
                                            <p style={{marginBottom: '4px'}}><strong
                                                style={{color: '#d9d9d9'}}>数量:</strong> <span
                                                style={{color: '#fff'}}>{action.quantity}</span></p>
                                        )}
                                        {(action.price !== null && action.price !== undefined) && (
                                            <p style={{marginBottom: '4px'}}><strong
                                                style={{color: '#d9d9d9'}}>价格:</strong> <span
                                                style={{color: '#fff'}}>{action.price}</span></p>
                                        )}
                                        {(action.confidence !== null && action.confidence !== undefined) && (
                                            <p style={{marginBottom: '4px'}}><strong
                                                style={{color: '#d9d9d9'}}>置信度:</strong> <span
                                                style={{color: '#fff'}}>{action.confidence}%</span></p>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>

                    <div style={{marginTop: '12px', borderTop: '1px solid #404040', paddingTop: '12px'}}>
                        {item.modelName && (
                            <p style={{marginBottom: '4px'}}><strong style={{color: '#d9d9d9'}}>执行模型:</strong> <span
                                style={{color: '#fff'}}>{item.modelName}</span></p>
                        )}
                    </div>

                    <p style={{color: 'var(--pt-amber)', marginTop: 10, fontWeight: 'bold'}}>
                        ⚠️ 注意: 此操作将使用当前系统配置执行真实交易!
                    </p>
                </div>
            ),
            okText: '确认重放',
            okType: 'primary',
            cancelText: '取消',
            onOk: async () => {
                try {
                    setReplaying(true);
                    const response = await botService.replayDecision(Number(item.decisionId));

                    if (response.success) {
                        messageApi.success('决策重放成功!');

                        // 显示执行结果详情
                        if (response.data && response.data.executionResults) {
                            Modal.info({
                                title: <span style={{color: '#ffffff'}}>重放执行结果</span>,
                                width: 600,
                                className: 'dark-theme-modal bot-confirm-modal',
                                centered: true,
                                content: (
                                    <div style={{color: '#a0a0a0'}}>
                                        <p style={{marginBottom: '4px'}}><strong
                                            style={{color: '#d9d9d9'}}>原始模型:</strong> {response.data.originalModelName}
                                        </p>
                                        <p style={{marginBottom: '4px'}}><strong
                                            style={{color: '#d9d9d9'}}>动作数量:</strong> {response.data.actionCount}
                                        </p>
                                        <p style={{marginBottom: '4px'}}><strong
                                            style={{color: '#d9d9d9'}}>执行时间:</strong> {response.data.executedAt}</p>
                                        <p style={{marginBottom: '4px'}}><strong
                                            style={{color: '#d9d9d9'}}>执行结果数量:</strong> {response.data.executionResults.length}
                                        </p>
                                    </div>
                                ),
                            });
                        }
                    } else {
                        messageApi.error('重放失败: ' + response.message);
                    }
                } catch (error) {
                    messageApi.error('重放失败: ' + (error instanceof Error ? error.message : '未知错误'));
                } finally {
                    setReplaying(false);
                }
            },
        });
    };

    // 单个action重放
    const handleSingleActionReplay = async (recordId: string | number, actionId: string | number, action: any) => {
        if (!actionId) {
            messageApi.warning('无法重放：缺少动作ID');
            return;
        }
        Modal.confirm({
            title: <span style={{color: '#ffffff'}}>确认重放此决策?</span>,
            className: 'bot-confirm-modal',
            centered: true,
            width: 560,
            icon: <ExclamationCircleOutlined style={{color: 'var(--pt-amber)'}}/>,
            content: (
                <div style={{color: '#a0a0a0'}}>
                    <p style={{marginBottom: '8px'}}>您即将重放以下决策:</p>
                    <div style={{
                        background: 'var(--pt-bg-hover)',
                        padding: '12px',
                        borderRadius: '4px',
                        marginBottom: '12px'
                    }}>
                        <p style={{marginBottom: '4px'}}>
                            <strong style={{color: '#d9d9d9'}}>动作类型:</strong>{' '}
                            <span style={{color: getActionColor(action.action)}}>
                                {action.action}
                            </span>
                        </p>
                        <p style={{marginBottom: '4px'}}>
                            <strong style={{color: '#d9d9d9'}}>合约:</strong>{' '}
                            <span style={{color: getInstIdColor(action.instId, action.posSide)}}>
                                {action.instId}
                            </span>
                        </p>
                        {action.posSide && (
                            <p style={{marginBottom: '4px'}}>
                                <strong style={{color: '#d9d9d9'}}>方向:</strong> {action.posSide}
                            </p>
                        )}
                        {action.quantity && action.quantity !== '-' && (
                            <p style={{marginBottom: '4px'}}>
                                <strong style={{color: '#d9d9d9'}}>数量:</strong> {action.quantity}
                            </p>
                        )}
                        {action.amount && action.amount !== '-' && (
                            <p style={{marginBottom: '4px'}}>
                                <strong style={{color: '#d9d9d9'}}>金额:</strong> {action.amount}
                            </p>
                        )}
                        {action.confidence && (
                            <p style={{marginBottom: '4px'}}>
                                <strong style={{color: '#d9d9d9'}}>置信度:</strong>{' '}
                                <span style={{color: getConfidenceColor(action.confidence)}}>
                                    {formatConfidence(action.confidence)}
                                </span>
                            </p>
                        )}
                    </div>
                    <p style={{color: 'var(--pt-amber)'}}>
                        ⚠️ 注意: 此操作将执行真实交易!
                    </p>
                </div>
            ),
            okText: '确认重放',
            cancelText: '取消',
            okButtonProps: {danger: true},
            cancelButtonProps: {
                style: {
                    backgroundColor: 'var(--pt-bg-panel)',
                    color: '#ffffff',
                    border: '1px solid var(--pt-border)'
                }
            },
            onOk: async () => {
                try {
                    const response = await botService.replaySingleAction(Number(recordId), Number(actionId));

                    if (response.success) {
                        messageApi.success('单个决策重放成功!');

                        // 显示执行结果详情
                        if (response.data) {
                            Modal.info({
                                title: <span style={{color: '#ffffff'}}>重放执行结果</span>,
                                width: 600,
                                className: 'dark-theme-modal',
                                centered: true,
                                content: (
                                    <div style={{color: '#a0a0a0'}}>
                                        <p style={{marginBottom: '4px'}}>
                                            <strong
                                                style={{color: '#d9d9d9'}}>动作类型:</strong> {response.data.actionType}
                                        </p>
                                        <p style={{marginBottom: '4px'}}>
                                            <strong style={{color: '#d9d9d9'}}>合约:</strong> {response.data.instId}
                                        </p>
                                        <p style={{marginBottom: '4px'}}>
                                            <strong
                                                style={{color: '#d9d9d9'}}>原始模型:</strong> {response.data.originalModelName}
                                        </p>
                                        <p style={{marginBottom: '4px'}}>
                                            <strong
                                                style={{color: '#d9d9d9'}}>执行时间:</strong> {response.data.executedAt}
                                        </p>
                                        {response.data.executionResult && (
                                            <p style={{marginBottom: '4px'}}>
                                                <strong style={{color: '#d9d9d9'}}>执行状态:</strong>{' '}
                                                <span style={{
                                                    color: response.data.executionResult.success ? 'var(--pt-bull)' : 'var(--pt-bear)'
                                                }}>
                                                    {response.data.executionResult.success ? '成功' : '失败'}
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                ),
                            });
                        }
                    } else {
                        messageApi.error('重放失败: ' + response.message);
                    }
                } catch (error) {
                    messageApi.error('重放失败: ' + (error instanceof Error ? error.message : '未知错误'));
                }
            },
        });
    };

    // 加载action执行历史
    const handleLoadActionHistory = async (actionId: number) => {
        if (!actionId) {
            messageApi.info('暂无执行历史');
            return;
        }
        // 如果已经展开,先收起;否则准备展开
        const key = String(actionId);
        if (expandedRowKeys.includes(key)) {
            // 收起
            setExpandedRowKeys(prev => prev.filter(k => k !== key));
            return;
        }

        // 标记加载中
        setLoadingHistoryMap(prev => ({...prev, [actionId]: true}));

        try {
            const response = await botService.getActionHistory(actionId, item.apiKeyId);

            if (response.success && response.data) {
                setActionHistoryMap(prev => ({...prev, [actionId]: response.data}));
                // 加载成功后立即展开该行
                setExpandedRowKeys(prev => [...prev, key]);
            } else {
                messageApi.error('加载执行历史失败: ' + response.message);
            }
        } catch (error) {
            // console.error('加载Action执行历史失败:', error);
            messageApi.error('加载执行历史失败: ' + (error instanceof Error ? error.message : '未知错误'));
        } finally {
            setLoadingHistoryMap(prev => ({...prev, [actionId]: false}));
        }
    };

    // 调用大模型
    const handleCallModel = async () => {
        if (callingModel) return;

        try {
            setCallingModel(true);
            if (onCallModel) {
                await onCallModel(item.decisionId);
            } else {
                messageApi.info('调用大模型功能暂未实现');
            }
        } catch (error) {
            // console.error('调用大模型失败:', error);
            messageApi.error('调用大模型失败');
        } finally {
            setCallingModel(false);
        }
    };

    const fetchRelatedOrders = async () => {
        if (!item.decisionId) {
            setRelatedOrders([]);
            return;
        }
        setRelatedLoading(true);
        try {
            const resp = await tradingService.getOrdersByRecordId(Number(item.decisionId));
            if (resp.data && resp.data.success) {
                setRelatedOrders(resp.data.data || []);
            } else {
                setRelatedOrders([]);
            }
        } catch (e) {
            // console.error('获取关联订单失败', e);
            setRelatedOrders([]);
        } finally {
            setRelatedLoading(false);
        }
    };
    const forecastStatusText = (() => {
        if (forecastSummaryLoading) return '预测加载中';
        if (forecastSummaries.some(f => f.validationStatus !== 'VALID')) return '预测无效';
        if (forecastSummaries.some(f => f.evaluationStatus === 'SKIPPED')) return '实际数据不足';
        if (forecastSummaries.some(f => f.evaluationStatus === 'FAILED')) return '评价失败';
        if (forecastSummaries.some(f =>
            f.evaluationStatus === 'EVALUATED' && f.directionHit === false)) return '方向未命中';
        if (forecastSummaries.length > 0
            && forecastSummaries.every(f =>
                f.evaluationStatus === 'EVALUATED' && f.directionHit === true)) return '方向命中';
        return '';
    })();
    return (
        <>
            <div
                style={{position: 'relative'}}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Card
                    size="small"
                    style={{
                        backgroundColor: 'var(--pt-bg-panel)',
                        border: '1px solid var(--pt-border)',
                        marginBottom: '12px',
                        borderRadius: '6px'
                    }}
                    styles={{body: {padding: '12px'}}}
                >
                    {/* 卡片头部 - 第一行：执行时间 | 模型 | 来源 | 状态 | 耗时 */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                    }}>
                        <Space size="small" wrap style={{flex: 1}}>
                            {/* 1. 执行时间 */}
                            <Space>
                                <ClockCircleOutlined style={{color: 'var(--pt-primary)'}}/>
                                <Text style={{color: 'rgba(255, 255, 255, 0.88)'}}>
                                    {formatTime(item.createdTime)}
                                </Text>
                            </Space>

                            {/* 2. AI模型 */}
                            {item.modelName && (
                                <Space>
                                    <RobotOutlined style={{color: 'var(--pt-purple)'}}/>
                                    <Text style={{color: 'rgba(255, 255, 255, 0.88)'}}>
                                        {item.modelName}
                                    </Text>
                                </Space>
                            )}

                            {/* 3. 调用来源 */}
                            {item.callSource && (
                                <Space size={4} wrap>
                                    {'AGENT_TRIGGER' === item.callSource
                                        ? (
                                            <Tag color="gold" style={{margin: 0}}>
                                                {item.agentName || item.agentCode || getSourceText(item.callSource)}
                                            </Tag>
                                        )
                                        : (
                                            <Tag color={getSourceColor(item.callSource)} style={{margin: 0}}>
                                                {getSourceText(item.callSource)}
                                            </Tag>
                                        )}
                                    {'TRACK_SCHEDULED' === item.callSource && (
                                        <>
                                            {item.instId && (() => {
                                                const direction = getTrackDirectionMeta(item.posSide);
                                                return (
                                                    <Tag
                                                        color={direction.text === '多' ? 'green' : direction.text === '空' ? 'red' : undefined}
                                                        style={{margin: 0, fontSize: 12}}
                                                    >
                                                        {item.instId}
                                                    </Tag>
                                                );
                                            })()}
                                            {item.trackUnitId !== null && item.trackUnitId !== undefined && (
                                                <Tag
                                                    style={{
                                                        margin: 0,
                                                        color: 'rgba(255, 255, 255, 0.62)',
                                                        fontSize: 12,
                                                        whiteSpace: 'nowrap',
                                                        fontFamily: 'monospace'
                                                    }}
                                                >
                                                    U#{item.trackUnitId}
                                                </Tag>
                                            )}
                                        </>
                                    )}
                                </Space>
                            )}

                            {/* 4. 执行状态 */}
                            {item.status === 'FAILED' && item.errorMessage ? (
                                <Tooltip
                                    title={`失败原因: ${item.errorMessage}`}
                                    color="red"
                                    classNames={{root: 'prompt-item-fail-tooltip'}}
                                    styles={{
                                        body: {
                                            minWidth: 500,
                                            maxWidth: 600,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }
                                    }}
                                >
                                    {/* 包一层原生 span：status 模式 Badge 不转发 ref，直接作为 Tooltip target 会触发
                                        antd rc-resize-observer 的 findDOMNode（StrictMode 下报 deprecated warning） */}
                                    <span style={{cursor: 'help'}}>
                                        <Badge
                                            status="error"
                                            text="失败"
                                        />
                                    </span>
                                </Tooltip>
                            ) : (
                                <Badge
                                    status={getStatusBadgeStatus(item.status)}
                                    text={getStatusText(item.status)}
                                    className={item.status === 'PROCESSING' ? 'prompt-item-badge-processing' : ''}
                                />
                            )}

                            {/* 假成功兜底提示：记录状态仍为 SUCCESS，但流程节点已失败
                                （历史数据 / 未知后处理异常），避免页面只显示“成功”却无执行结果 */}
                            {item.status === 'SUCCESS' && (() => {
                                const failedNode = item.flowNodes?.find(node => node.status === 'FAILED');
                                if (!failedNode) {
                                    return null;
                                }
                                return (
                                    <Tooltip
                                        title={`流程节点失败：${failedNode.nodeName || failedNode.nodeCode}${failedNode.message ? ` - ${failedNode.message}` : ''}`}
                                    >
                                        <Tag color="warning" style={{fontSize: '11px', margin: 0}}>
                                            流程异常
                                        </Tag>
                                    </Tooltip>
                                );
                            })()}

                            {/* 5. 处理耗时 */}
                            {item.processingTimeMs && (
                                <Text style={{
                                    color: item.processingTimeMs < 5000 ? 'var(--pt-bull)' : 'var(--pt-amber)',
                                    fontSize: '11px'
                                }}>
                                    {item.processingTimeMs < 1000
                                        ? `${item.processingTimeMs}ms`
                                        : `${(item.processingTimeMs / 1000).toFixed(2)}s`}
                                </Text>
                            )}
                        </Space>

                        {/* 折叠/展开按钮：已禁用状态的Item没有更多内容，无需展开 */}
                        {item.status !== 'AGENT_DISABLED' && (
                            <Button
                                type="text"
                                size="small"
                                icon={isExpanded ? <UpOutlined/> : <DownOutlined/>}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const newState = !isExpanded;
                                    setIsExpanded(newState);
                                    // 通知父组件展开状态变化，触发详情加载
                                    if (newState && onExpandedStateChange) {
                                        onExpandedStateChange('promptContent', true);
                                    }
                                }}
                                style={{color: isExpanded ? 'var(--pt-primary)' : 'rgba(255, 255, 255, 0.6)'}}
                                title={isExpanded ? "收起" : "展开"}
                            />
                        )}
                    </div>

                    {/* 卡片头部 - 第二行：Action Tag (动作 + 交易对 + 方向) + 会话链路/Track演化按钮（合并为一行） */}
                    {(() => {
                        // 会话链路/Track演化按钮（原独立一行，现并入 tag 行最前）
                        const hasChainLink = Boolean(onParentIdClick) && !item.trackUnitId;
                        const hasTrackLink = Boolean(item.trackUnitId && onTrackEvolutionClick);
                        const hasAnyLink = hasChainLink || hasTrackLink;
                        const chainLinkButton = hasChainLink && (
                            <Tooltip title="查看会话链路">
                                <Button
                                    type="text"
                                    icon={<BranchesOutlined style={{color: '#9254de'}}/>}
                                    onClick={() => onParentIdClick?.(Number(item.decisionId))}
                                    style={{padding: '4px 8px', color: 'rgba(255, 255, 255, 0.6)'}}
                                    size="small"
                                >
                                </Button>
                            </Tooltip>
                        );
                        const trackLinkButton = hasTrackLink && (
                            <Tooltip title="跳转Track决策演化">
                                <Button
                                    type="text"
                                    icon={<LinkOutlined style={{color: 'var(--pt-bull)'}}/>}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onTrackEvolutionClick?.({
                                            trackUnitId: item.trackUnitId!,
                                            trackRunId: item.trackRunId ?? 0,
                                        });
                                    }}
                                    style={{padding: '4px 8px', color: 'rgba(255, 255, 255, 0.6)'}}
                                    size="small"
                                >
                                </Button>
                            </Tooltip>
                        );

                        // 优先解析完整的决策JSON
                        const decisionData = normalizeActionField(parseDecisionJson(item.responseSegments));
                        let actionObjects: any[] = [];

                        if (decisionData) {
                            if (Array.isArray(decisionData)) {
                                actionObjects = decisionData;
                            } else if (decisionData.actions && Array.isArray(decisionData.actions)) {
                                actionObjects = decisionData.actions;
                            } else if (decisionData.action) {
                                actionObjects = [decisionData];
                            }
                        }

                        // 降级处理：如果没有解析到完整对象，使用字符串方式
                        if (actionObjects.length === 0) {
                            const displayAction = item.action || parseActionFromSegments(item.responseSegments);
                            if (!displayAction) {
                                // 无 action 数据但存在链接按钮时保留空数组继续（仅渲染按钮行）
                                if (!hasAnyLink) return null;
                            } else {
                                const actionStrings = displayAction.split(',').map(a => a.trim()).filter(a => a);
                                // 将字符串转换为简单对象，补充 item 中的字段
                                actionObjects = actionStrings.map(actionStr => ({
                                    action: actionStr,
                                    instId: item.instId,
                                    posSide: item.posSide,
                                    price: item.price,
                                    quantity: item.quantity
                                }));
                            }
                        }

                        if (actionObjects.length === 0 && !hasAnyLink) return null;

                        // 过滤掉没有有效action字段的空动作对象（如 {"actions": [{}]}）
                        actionObjects = actionObjects.filter(a => a && a.action);
                        if (actionObjects.length === 0 && !hasAnyLink) return null;

                        // Prompt 历史会隐藏 TRACK 标签；平仓/调仓 Run 需要补出真实买卖方向。
                        if (hideTrackActionTag) {
                            actionObjects = withTrackTradePresentation(actionObjects, item);
                        }

                        // 按action类型分组
                        const actionGroups = actionObjects.reduce((groups: any, actionObj: any) => {
                            const actionKey = getActionGroupKey(actionObj);
                            if (!groups[actionKey]) {
                                groups[actionKey] = [];
                            }
                            groups[actionKey].push(actionObj);
                            return groups;
                        }, {});

                        // 获取去重的action类型列表
                        const uniqueActionTypes = Object.keys(actionGroups).filter(
                            actionType => !(hideTrackActionTag && actionType.toUpperCase() === 'TRACK')
                        );

                        const hasTradeAction = uniqueActionTypes.some(isTradeActionKey);
                        const hasOpenClose = Boolean(item.openCloses && item.openCloses.trim());
                        const hasRiskStatus = hasTradeAction
                            && Boolean(item.riskControlStatus && getRiskControlStatusText(item.riskControlStatus));
                        const hasTradeActionStatus = hasTradeAction
                            && Boolean(item.tradeActionStatus && getTradeActionStatusText(item.tradeActionStatus));

                        // Track 记录被隐藏 TRACK 标签且无开平仓/状态/链接按钮时，不再渲染空白行
                        if (uniqueActionTypes.length === 0
                            && !hasOpenClose
                            && !hasRiskStatus
                            && !hasTradeActionStatus
                            && !hasChainLink
                            && !hasTrackLink) {
                            return null;
                        }

                        // 渲染去重的action tags（买入/卖出 tag 合并品种名，方向沿用 Tag 背景色）
                        const actionTags = (
                            <Space size="small">
                                {uniqueActionTypes.map((actionType) => {
                                    const actionsForType = actionGroups[actionType];
                                    const count = actionsForType.length;
                                    const actionText = getTradeActionText(actionsForType[0]);
                                    const baseType = actionType.split('|')[0];
                                    const instId = String(actionsForType[0]?.instId || '').trim();
                                    const instIdSuffix = (baseType === 'BUY' || baseType === 'SELL') && instId
                                        ? ` ${instId}` : '';
                                    const countSuffix = count > 1 ? `(${count})` : '';
                                    const displayText = `${actionText}${instIdSuffix}${countSuffix}`;
                                    const tagColor = getActionColor(baseType);

                                    return (
                                        <Popover
                                            key={actionType}
                                            content={renderActionPopoverContent(actionsForType)}
                                            trigger="hover"
                                            classNames={{root: 'dark-theme-popover'}}
                                            styles={{root: {maxWidth: '700px'}}}
                                        >
                                            <Tag color={tagColor} style={{fontSize: '13px'}}>
                                                {displayText}
                                            </Tag>
                                        </Popover>
                                    );
                                })}
                            </Space>
                        );

                        return (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px'
                            }}>
                                {/* 左侧 (50%宽度) */}
                                <div style={{flex: 1, minWidth: 0}}>
                                    <Space size="small" wrap>
                                        {/* 会话链路 / Track 演化按钮（与 action tag 同行） */}
                                        {chainLinkButton}
                                        {trackLinkButton}

                                        {/* 决策动作 */}
                                        {actionTags}

                                        {/* 开平仓类型标签 */}
                                        {item.openCloses && (
                                            <Space size="small">
                                                {item.openCloses.split(',').map((oc, index) => {
                                                    // 修复bug：智能匹配action对象，确保reasoning字段正确传递
                                                    const direction = oc.trim(); // 'open' 或 'close'

                                                    // 从actionObjects中找到匹配的action（根据posSide或action类型）
                                                    const matchedAction = actionObjects.find(action => {
                                                        const actionPosSide = action.posSide?.toLowerCase();
                                                        const actionType = action.action?.toUpperCase();

                                                        if (direction === 'open') {
                                                            // 开仓匹配long或BUY
                                                            return actionPosSide === 'long' || actionType === 'BUY';
                                                        } else {
                                                            // 平仓匹配short或SELL
                                                            return actionPosSide === 'short' || actionType === 'SELL';
                                                        }
                                                    }) || actionObjects[0]; // 兜底：取第一个

                                                    // 确保action对象包含reasoning字段
                                                    const actionWithReasoning = {
                                                        ...matchedAction,
                                                        reasoning: matchedAction?.reasoning || item.reasoning || ''
                                                    };

                                                    return (
                                                        <Popover
                                                            key={`openclose-${index}`}
                                                            content={renderActionPopoverContent(actionWithReasoning)}
                                                            trigger="hover"
                                                            classNames={{root: 'dark-theme-popover'}}
                                                            styles={{root: {maxWidth: '700px'}}}
                                                        >
                                                            <Tag
                                                                color={direction === 'open' ? 'green' : 'red'}
                                                                style={{cursor: 'pointer', fontSize: '13px'}}
                                                            >
                                                                {direction === 'open' ? '开仓' : '平仓'}
                                                            </Tag>
                                                        </Popover>
                                                    );
                                                })}
                                            </Space>
                                        )}
                                    </Space>
                                </div>

                                {/* 右侧 (50%宽度) - 显示两个状态 */}
                                <div style={{flex: 1, textAlign: 'right', minWidth: 0}}>
                                    <Space size="small">
                                        {/* 风控状态 - 仅交易动作显示(BUY/SELL/CANCEL_ORDER) */}
                                        {hasRiskStatus && (
                                            <Tag
                                                color={getRiskControlStatusColor(item.riskControlStatus)}
                                                style={{fontSize: '11px', cursor: 'pointer'}}
                                                onClick={() => {
                                                    // 打开风控信息弹窗
                                                    setRiskControlRecordId(Number(item.decisionId));
                                                    setRiskControlApiKeyId(item.apiKeyId || null);
                                                    setRiskControlInfoVisible(true);
                                                }}
                                            >
                                                {getRiskControlStatusText(item.riskControlStatus)}
                                            </Tag>
                                        )}

                                        {/* 交易动作状态 - 仅交易动作显示(BUY/SELL/CANCEL_ORDER) */}
                                        {hasTradeActionStatus && (() => {
                                            const trackExecFailed = item.tradeActionStatus === 'FAILED'
                                                && (item.trackErrorCode || item.trackErrorMessage);
                                            return (
                                                <Tooltip
                                                    title={trackExecFailed
                                                        ? `${item.trackErrorCode || 'Track 执行异常'}${item.trackErrorMessage ? `: ${item.trackErrorMessage}` : ''}`
                                                        : getTradeActionStatusText(item.tradeActionStatus)}
                                                >
                                                    <Tag color={getTradeActionStatusColor(item.tradeActionStatus)}
                                                         style={{fontSize: '11px'}}>
                                                        {getTradeActionStatusText(item.tradeActionStatus)}
                                                    </Tag>
                                                </Tooltip>
                                            );
                                        })()}
                                    </Space>
                                </div>
                            </div>
                        );
                    })()}

                    {/* 账户数据 - 仅当有数据时显示 */}
                    {(item.inputTotalEquity !== null && item.inputTotalEquity !== undefined) && (
                        <div style={{
                            border: '1px dashed var(--pt-border)',
                            backgroundColor: 'var(--pt-bg-hover)',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            width: '100%',
                            minWidth: '0',
                            boxSizing: 'border-box'
                        }}>
                            <Space size="small" style={{flexWrap: 'nowrap'}}>
                                {item.inputTotalEquity !== null && item.inputTotalEquity !== undefined && (
                                    <Text style={{
                                        color: 'rgba(255, 255, 255, 0.65)',
                                        fontSize: '11px',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        权益: <Text style={{
                                        color: 'var(--pt-primary)',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap'
                                    }}>{item.inputTotalEquity.toFixed(2)}</Text>
                                    </Text>
                                )}

                                {item.inputUnrealizedPnl !== null && item.inputUnrealizedPnl !== undefined && (
                                    <Text style={{
                                        color: 'rgba(255, 255, 255, 0.65)',
                                        fontSize: '11px',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        未结盈亏: <Text style={{
                                        color: item.inputUnrealizedPnl >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {item.inputUnrealizedPnl >= 0 ? '+' : ''}{item.inputUnrealizedPnl.toFixed(2)}
                                    </Text>
                                    </Text>
                                )}

                                {item.inputUsedMargin !== null && item.inputUsedMargin !== undefined && (
                                    <Text style={{
                                        color: 'rgba(255, 255, 255, 0.65)',
                                        fontSize: '11px',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        保证金: <Text style={{
                                        color: 'rgba(255, 255, 255, 0.88)',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap'
                                    }}>{item.inputUsedMargin.toFixed(2)}</Text>
                                    </Text>
                                )}

                                {/*{item.inputAvailableBalance !== null && item.inputAvailableBalance !== undefined && (*/}
                                {/*    <Text style={{color: 'rgba(255, 255, 255, 0.65)', fontSize: '11px', whiteSpace: 'nowrap'}}>*/}
                                {/*        可用: <Text style={{*/}
                                {/*        color: 'var(--pt-bull)',*/}
                                {/*        fontWeight: 'bold',*/}
                                {/*        whiteSpace: 'nowrap'*/}
                                {/*    }}>{item.inputAvailableBalance.toFixed(2)}</Text>*/}
                                {/*    </Text>*/}
                                {/*)}*/}

                                {item.inputMarginRatio !== null && item.inputMarginRatio !== undefined && (
                                    <Text style={{
                                        color: 'rgba(255, 255, 255, 0.65)',
                                        fontSize: '11px',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        使用率: <Text style={{
                                        color: 'var(--pt-amber)',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap'
                                    }}>{item.inputMarginRatio.toFixed(2)}%</Text>
                                    </Text>
                                )}
                            </Space>
                        </div>
                    )}

                    {/* 风险等级 - 始终显示 */}
                    {item.decisionRiskLevel && (
                        <div style={{marginBottom: '12px'}}>
                            <Space size="middle">
                                <Text style={{color: 'rgba(255, 255, 255, 0.65)', fontSize: '13px'}}>风险等级:
                                    <Tag color={getRiskColor(item.decisionRiskLevel)}
                                         style={{marginLeft: '8px', marginRight: 0}}>
                                        {getRiskText(item.decisionRiskLevel)}
                                    </Tag>
                                </Text>

                                {item.decisionConfidence && (
                                    <Text style={{color: 'rgba(255, 255, 255, 0.65)', fontSize: '13px'}}>
                                        置信度: <span style={{
                                        color: 'rgba(255, 255, 255, 0.88)',
                                        fontWeight: 'bold'
                                    }}>{formatConfidence(item.decisionConfidence)}</span>
                                    </Text>
                                )}
                            </Space>
                        </div>
                    )}

                    {/* Tab内容区 - 仅展开时显示 */}
                    {isExpanded && (
                        <>
                            {/* 分隔线 */}
                            <Divider style={{margin: '12px 0', borderColor: 'var(--pt-border)'}}/>

                            {/* Prompt内容与AI模型响应 - Tab展示 */}
                            <Tabs
                                defaultActiveKey="aiResponse"
                                type="line"
                                size="small"
                                className="dark-theme-tabs"
                                onChange={(key) => {
                                    setActiveTabKey(key);
                                    if (key === 'orders' && relatedOrders.length === 0 && !relatedLoading) {
                                        fetchRelatedOrders();
                                    }
                                }}
                                tabBarExtraContent={
                                    <Space size="small">
                                        {item.status === 'SUCCESS' && hasForecastCandidate && (
                                            <Tooltip title="查看模型预测及实际K线跟踪结果">
                                                <Button
                                                    type="default"
                                                    size="small"
                                                    icon={<LineChartOutlined/>}
                                                    onClick={() => setForecastVisible(true)}
                                                >
                                                    {forecastStatusText}
                                                </Button>
                                            </Tooltip>
                                        )}
                                        {/* 重放决策按钮 - 仅在决策JSON存在时显示，且仅在AI响应Tab激活且状态为成功时显示 */}
                                        {!publicView && activeTabKey === 'aiResponse' && item.status === 'SUCCESS' && item.responseSegments && item.responseSegments.some(seg => seg.title === '决策JSON') && (
                                            <Tooltip title="从历史AI响应重新执行交易决策">
                                                <Button
                                                    type="primary"
                                                    danger
                                                    size="small"
                                                    icon={<RedoOutlined/>}
                                                    onClick={handleReplay}
                                                    loading={replaying}
                                                    style={{fontSize: '11px', backgroundColor: 'var(--pt-bg-panel)'}}
                                                >
                                                </Button>
                                            </Tooltip>
                                        )}
                                        {/*{item.status === 'PROCESSING' && (*/}
                                        {/*    <Button*/}
                                        {/*        type="link"*/}
                                        {/*        size="small"*/}
                                        {/*        icon={<PlayCircleOutlined/>}*/}
                                        {/*        onClick={handleCallModel}*/}
                                        {/*        loading={callingModel}*/}
                                        {/*        style={{padding: '0 4px'}}*/}
                                        {/*    >*/}
                                        {/*        {callingModel ? '调用中...' : '调用大模型'}*/}
                                        {/*    </Button>*/}
                                        {/*)}*/}
                                    </Space>
                                }
                                items={[
                                    // Tab 1: Prompt内容
                                    (item.promptContent || parsedSegments.length > 0 || hasPromptSections) ? {
                                        key: 'prompt',
                                        label: (
                                            <Space>
                                                <DatabaseOutlined/>
                                                <Text style={{color: 'rgba(255, 255, 255, 0.88)'}}>
                                                    {promptTitle}
                                                </Text>
                                                {parsedSegments.length > 0 && (
                                                    <Tag color="blue" style={{fontSize: '11px', margin: 0}}>
                                                        {parsedSegments.length}
                                                    </Tag>
                                                )}
                                            </Space>
                                        ),
                                        children: (
                                            <div style={{marginTop: '8px'}}>
                                                {/* 模式切换和复制按钮 */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: '12px'
                                                }}>
                                                    <Space size="small">
                                                        {/* 显示模式切换 */}
                                                        {!hasPromptSections && parsedSegments.length > 0 && (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<SwapOutlined/>}
                                                                onClick={() => setPromptDisplayMode(
                                                                    promptDisplayMode === 'segments' ? 'raw' : 'segments'
                                                                )}
                                                                style={{
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    fontSize: '11px'
                                                                }}
                                                            >
                                                                {promptDisplayMode === 'segments' ? '原始文本' : '结构化'}
                                                            </Button>
                                                        )}
                                                    </Space>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<CopyOutlined/>}
                                                        onClick={() => {
                                                            const content = hasPromptSections
                                                                ? buildPromptSectionsCopyText()
                                                                : promptDisplayMode === 'segments' && parsedSegments.length > 0
                                                                    ? parsedSegments.map(s =>
                                                                        `=== ${s.title} ===\n${s.content}`
                                                                    ).join('\n\n')
                                                                    : item.promptContent || (parsedSegments.length > 0 ? parsedSegments.map(s => `=== ${s.title} ===\n${s.content}`).join('\n\n') : '');
                                                            handleCopy(content, promptTitle);
                                                        }}
                                                        style={{color: 'rgba(255, 255, 255, 0.6)'}}
                                                    >
                                                        复制
                                                    </Button>
                                                </div>

                                                {/* Prompt具体内容 */}
                                                {hasPromptSections ? (
                                                    <AgentExecutionPromptView
                                                        sections={item.promptSections || []}
                                                        apiKeyId={item.apiKeyId}
                                                    />
                                                ) : promptDisplayMode === 'segments' && parsedSegments.length > 0 ? (
                                                    <SegmentEditor
                                                        segments={parsedSegments}
                                                        readOnly={true}
                                                        apiKeyId={item.apiKeyId}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        backgroundColor: '#2a2a2a',
                                                        padding: '12px',
                                                        borderRadius: '4px',
                                                        fontFamily: 'monospace',
                                                        fontSize: '12px',
                                                        color: 'rgba(255, 255, 255, 0.8)',
                                                        whiteSpace: 'pre-wrap',
                                                        overflowY: 'auto',
                                                        maxHeight: '400px',
                                                        border: '1px solid #404040'
                                                    }}>
                                                        {item.promptContent || (parsedSegments.length > 0
                                                            ? parsedSegments.map(s => `=== ${s.title} ===\n${s.content}`).join('\n\n')
                                                            : `暂无${promptTitle}`)}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    } : null,
                                    // Tab 2: AI模型响应
                                    {
                                        key: 'aiResponse',
                                        label: (
                                            <Space>
                                                <RobotOutlined/>
                                                <Text style={{color: 'rgba(255, 255, 255, 0.88)'}}>
                                                    模型响应
                                                </Text>
                                                {item.responseSegments && item.responseSegments.length > 0 && (
                                                    <Tag color="purple" style={{fontSize: '11px', margin: 0}}>
                                                        {item.responseSegments.length}
                                                    </Tag>
                                                )}
                                            </Space>
                                        ),
                                        children: (
                                            <div style={{marginTop: '8px'}}>
                                                {/* 处理中状态 */}
                                                {item.status === 'PROCESSING' && !item.responseSegments && (
                                                    <div style={{
                                                        padding: '20px',
                                                        textAlign: 'center',
                                                        color: 'rgba(255, 255, 255, 0.6)'
                                                    }}>
                                                        Loading...
                                                    </div>
                                                )}

                                                {/* AI响应内容展示 */}
                                                {(item.responseSegments && item.responseSegments.length > 0) || item.fullResponse ? (
                                                    <>
                                                        {/* 控制栏：模式切换和复制按钮 */}
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            marginBottom: '12px'
                                                        }}>
                                                            <Space size="small">
                                                                {/* 显示模式切换按钮 */}
                                                                {item.responseSegments && item.responseSegments.length > 0 && (
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<SwapOutlined/>}
                                                                        onClick={() => setAiResponseDisplayMode(
                                                                            aiResponseDisplayMode === 'segments' ? 'raw' : 'segments'
                                                                        )}
                                                                        style={{
                                                                            color: 'rgba(255, 255, 255, 0.6)',
                                                                            fontSize: '11px'
                                                                        }}
                                                                    >
                                                                        {aiResponseDisplayMode === 'segments' ? '原始文本' : '结构化'}
                                                                    </Button>
                                                                )}
                                                            </Space>
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<CopyOutlined/>}
                                                                onClick={() => {
                                                                    const content = aiResponseDisplayMode === 'segments' && item.responseSegments
                                                                        ? item.responseSegments.map(s =>
                                                                            `=== ${s.title} ===\n${s.content}`
                                                                        ).join('\n\n')
                                                                        : item.fullResponse || (item.responseSegments ? item.responseSegments.map(s => `=== ${s.title} ===\n${s.content}`).join('\n\n') : '');
                                                                    handleCopy(content, 'AI模型响应');
                                                                }}
                                                                style={{color: 'rgba(255, 255, 255, 0.6)'}}
                                                            >
                                                                复制
                                                            </Button>
                                                        </div>

                                                        {/* 内容展示 */}
                                                        {aiResponseDisplayMode === 'segments' && item.responseSegments && item.responseSegments.length > 0 ? (
                                                            <SegmentEditor
                                                                segments={item.responseSegments}
                                                                readOnly={true}
                                                                apiKeyId={item.apiKeyId}
                                                            />
                                                        ) : (
                                                            <div style={{
                                                                backgroundColor: '#2a2a2a',
                                                                padding: '12px',
                                                                borderRadius: '4px',
                                                                fontFamily: 'monospace',
                                                                fontSize: '12px',
                                                                color: 'rgba(255, 255, 255, 0.8)',
                                                                whiteSpace: 'pre-wrap',
                                                                overflowY: 'auto',
                                                                maxHeight: '400px',
                                                                border: '1px solid #404040'
                                                            }}>
                                                                {item.fullResponse || (item.responseSegments && item.responseSegments.length > 0
                                                                    ? item.responseSegments.map(s => `=== ${s.title} ===\n${s.content}`).join('\n\n')
                                                                    : '暂无AI响应内容')}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    item.status !== 'PROCESSING' && (
                                                        <div style={{
                                                            padding: '20px',
                                                            textAlign: 'center',
                                                            color: 'rgba(255, 255, 255, 0.6)'
                                                        }}>
                                                            暂无AI响应内容
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        )
                                    }
                                    ,
                                    {
                                        key: 'orders',
                                        label: (
                                            <Space>
                                                <UnorderedListOutlined/>
                                                <Text style={{color: 'rgba(255, 255, 255, 0.88)'}}>
                                                    关联订单
                                                </Text>
                                                {(item.relatedOrderCount || relatedOrders.length > 0) && (
                                                    <Tag color="green" style={{fontSize: '11px', margin: 0}}>
                                                        {item.relatedOrderCount || relatedOrders.length}
                                                    </Tag>
                                                )}
                                            </Space>
                                        ),
                                        children: (
                                            <div style={{marginTop: '8px'}}>
                                                {relatedLoading ? (
                                                    <div style={{
                                                        padding: '20px',
                                                        textAlign: 'center',
                                                        color: 'rgba(255, 255, 255, 0.6)'
                                                    }}>加载中...</div>
                                                ) : (
                                                    relatedOrders.length > 0 ? (
                                                        <Table
                                                            rowKey={(r) => String((r as any).id || (r as any).orderUuid)}
                                                            columns={[
                                                                {
                                                                    title: '订单ID',
                                                                    dataIndex: 'orderUuid',
                                                                    key: 'orderUuid',
                                                                    width: 150,
                                                                    ellipsis: true,
                                                                    render: (text: string, record: any) => (
                                                                        <span
                                                                            style={{
                                                                                color: 'var(--pt-primary)',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                            onClick={() => handleOpenOrderDetail(record)}
                                                                        >
                                                            {text}
                                                        </span>
                                                                    )
                                                                },
                                                                {
                                                                    title: '合约',
                                                                    dataIndex: 'instId',
                                                                    key: 'instId',
                                                                    width: 140,
                                                                    render: (text: string, record: any) => (
                                                                        <span
                                                                            style={{color: getInstIdColor(text, record.posSide)}}>
                                                            {text}
                                                        </span>
                                                                    )
                                                                },
                                                                {
                                                                    title: '买卖',
                                                                    dataIndex: 'side',
                                                                    key: 'side',
                                                                    width: 60,
                                                                    render: (text: string) => (
                                                                        <span
                                                                            style={{color: text === 'buy' ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>
                                                            {text === 'buy' ? '买入' : '卖出'}
                                                        </span>
                                                                    )
                                                                },
                                                                {
                                                                    title: '方向',
                                                                    dataIndex: 'posSide',
                                                                    key: 'posSide',
                                                                    width: 60,
                                                                    render: (text: string) => (
                                                                        <span
                                                                            style={{color: text === 'long' ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>
                                                            {text === 'long' ? '多' : '空'}
                                                        </span>
                                                                    )
                                                                },
                                                                {title: '张数', dataIndex: 'sz', key: 'sz', width: 80},
                                                                {
                                                                    title: '金额',
                                                                    dataIndex: 'amt',
                                                                    key: 'amt',
                                                                    width: 100
                                                                },
                                                                {
                                                                    title: '杠杆',
                                                                    dataIndex: 'lever',
                                                                    key: 'lever',
                                                                    width: 60
                                                                },
                                                                {
                                                                    title: '创建时间',
                                                                    dataIndex: 'createdTime',
                                                                    key: 'createdTime',
                                                                    width: 160,
                                                                    render: (t: any) => <span style={{
                                                                        color: '#a0a0a0',
                                                                        fontSize: '11px'
                                                                    }}>{formatTime(t)}</span>
                                                                }
                                                            ]}
                                                            dataSource={relatedOrders}
                                                            pagination={false}
                                                            size="small"
                                                            bordered={false}
                                                            showHeader={true}
                                                            tableLayout="fixed"
                                                            scroll={{x: 'max-content'}}
                                                            style={{
                                                                background: 'var(--pt-bg-panel)',
                                                                fontSize: '12px',
                                                                width: '100%'
                                                            }}
                                                            className="dark-theme-action-table"
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            padding: '20px',
                                                            textAlign: 'center',
                                                            color: 'rgba(255, 255, 255, 0.6)'
                                                        }}>暂无关联订单</div>
                                                    )
                                                )}
                                            </div>
                                        )
                                    }
                                ].filter(Boolean) as any}
                            />
                        </>
                    )}
                </Card>
                {deletable && deleteOverlayVisible && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 12,
                        borderRadius: '6px',
                        backgroundColor: 'rgba(12, 17, 32, 0.78)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 5
                    }}>
                        <Button
                            danger
                            type="primary"
                            icon={<DeleteOutlined/>}
                            onClick={handleDelete}
                        >
                            删除
                        </Button>
                    </div>
                )}
            </div>

            {/* Reasoning内容展示Modal */}
            {renderOrderDetailModal()}
            <Modal
                title="决策推理过程"
                open={reasoningModalVisible}
                onCancel={() => setReasoningModalVisible(false)}
                footer={[
                    <Button key="copy" type="primary" onClick={() => {
                        navigator.clipboard.writeText(currentReasoning);
                        messageApi.success('已复制到剪贴板');
                    }}>
                        复制
                    </Button>,
                    <Button key="close" onClick={() => setReasoningModalVisible(false)}>
                        关闭
                    </Button>
                ]}
                width={800}
            >
                <div style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                    {currentReasoning}
                </div>
            </Modal>

            {/* 风控信息弹窗 */}
            <RiskControlInfoModal
                visible={riskControlInfoVisible}
                recordId={riskControlRecordId}
                apiKeyId={riskControlApiKeyId}
                onClose={() => {
                    setRiskControlInfoVisible(false);
                    setRiskControlRecordId(null);
                    setRiskControlApiKeyId(null);
                }}
            />
            <KlineForecastModal
                visible={forecastVisible}
                decisionId={item.decisionId}
                onClose={() => setForecastVisible(false)}
            />
        </>
    );
};

export default PromptItem;
